// A steam room finish's textures, generated (docs/claude/woods.md, *Steam room
// finishes*): each surface a recipe (src/shared/finishes.ts) whose figures were
// measured off a photograph, evaluated as a periodic function of a point in mm
// and rasterised in RGBA. Pure and DOM-free: the 3D view turns the pixels into
// textures; every tile is seamless by construction and the same recipe always
// gives the same pixels.

import type {
  BeadsRecipe,
  CementRecipe,
  SurfaceRecipe,
} from '../shared/finishes';

export type RGB = [number, number, number];

/** A surface: its colour at a point (mm), repeating every `period` mm both ways. */
export interface SurfaceField {
  period: number;
  at(x: number, y: number): RGB;
}

/** Pixels, row by row from the top, RGBA. */
export interface Raster { width: number; height: number; data: Uint8ClampedArray<ArrayBuffer> }

/** Every surface repeats this often (mm): a whole number of beads and of noise
 *  cells, and long enough not to read as a pattern on a wall. */
export const PERIOD = 160;

const rgbOf = (hex: string): RGB => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const smooth = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
const wrap = (v: number, n: number): number => ((v % n) + n) % n;

/** A seeded random stream in [0, 1) (mulberry32). */
function randoms(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Gradient (Perlin) noise of cells `cell` mm, repeating every `PERIOD`, scaled
 *  to a standard deviation of 1 (measured on a fixed sample of points). */
function gradientNoise(cell: number, rnd: () => number): (x: number, y: number) => number {
  const n = Math.max(1, Math.round(PERIOD / cell)), size = PERIOD / n;
  const gx = new Float64Array(n * n), gy = new Float64Array(n * n);
  for (let i = 0; i < n * n; i++) { const a = rnd() * 2 * Math.PI; gx[i] = Math.cos(a); gy[i] = Math.sin(a); }
  const q = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
  const raw = (x: number, y: number): number => {
    const fx = x / size, fy = y / size, i = Math.floor(fx), j = Math.floor(fy), u = fx - i, v = fy - j;
    const i0 = wrap(i, n), i1 = wrap(i + 1, n), j0 = wrap(j, n) * n, j1 = wrap(j + 1, n) * n;
    const d00 = gx[j0 + i0] * u + gy[j0 + i0] * v, d10 = gx[j0 + i1] * (u - 1) + gy[j0 + i1] * v;
    const d01 = gx[j1 + i0] * u + gy[j1 + i0] * (v - 1), d11 = gx[j1 + i1] * (u - 1) + gy[j1 + i1] * (v - 1);
    const tx = q(u), ty = q(v), top = d00 + (d10 - d00) * tx, bottom = d01 + (d11 - d01) * tx;
    return top + (bottom - top) * ty;
  };
  let s = 0, s2 = 0;
  const k = 4096, probe = randoms(7);
  for (let i = 0; i < k; i++) { const r = raw(probe() * PERIOD, probe() * PERIOD); s += r; s2 += r * r; }
  const mean = s / k, sd = Math.sqrt(Math.max(1e-12, s2 / k - mean * mean));
  return (x, y) => (raw(x, y) - mean) / sd;
}

/** Sand: grains about `size` mm across, packed on a jittered grid, each the
 *  points nearest its seed (Voronoi) and each one brightness — mean 0 and
 *  standard deviation 1, but a `dark` share of them `depth` darker (in units of
 *  the grain's amplitude) — repeating every `PERIOD`. */
function sandNoise(size: number, dark: number, depth: number, rnd: () => number): (x: number, y: number) => number {
  const n = Math.max(1, Math.round(PERIOD / size)), s = PERIOD / n;
  const sx = new Float64Array(n * n), sy = new Float64Array(n * n), v = new Float64Array(n * n);
  for (let i = 0; i < n * n; i++) {
    sx[i] = ((i % n) + 0.5 + (rnd() - 0.5) * 0.9) * s;
    sy[i] = (Math.floor(i / n) + 0.5 + (rnd() - 0.5) * 0.9) * s;
    v[i] = rnd() < dark ? -depth * (0.6 + 0.8 * rnd()) : (rnd() * 2 - 1) * Math.sqrt(3);
  }
  return (x, y) => {
    const ci = Math.floor(x / s), cj = Math.floor(y / s);
    let best = Infinity, k = 0;
    for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
      const i = ci + di, j = cj + dj, wi = wrap(i, n), wj = wrap(j, n), c = wj * n + wi;
      const dx = x - sx[c] - (i - wi) * s, dy = y - sy[c] - (j - wj) * s, d = dx * dx + dy * dy;
      if (d < best) { best = d; k = c; }
    }
    return v[k];
  };
}

/** Cement: its colour, brightened and darkened by noise at each of its scales
 *  and by its sand, with dark pores of each population scattered over it. */
function cementField(r: CementRecipe): SurfaceField {
  const rnd = randoms(r.seed);
  const base = rgbOf(r.color);
  const octaves = r.octaves.map(([cell, amp]) => ({ f: gradientNoise(cell, rnd), amp }));
  const sand = sandNoise(r.sand.size, r.sand.dark, r.sand.depth, rnd), sandAmp = r.sand.amp;
  // Pores, kept by the 1 mm cell their middle is in, found from the cells round a point.
  const cells = PERIOD, pores: Array<Array<{ x: number; y: number; r: number; depth: number }>> = Array.from({ length: cells * cells }, () => []);
  let maxR = 0;
  for (const pop of r.pores) {
    const count = Math.round((pop.perCm2 / 100) * PERIOD * PERIOD);
    for (let k = 0; k < count; k++) {
      // A pore is a cluster of one to three overlapping pits: irregular, as cast.
      const x = rnd() * PERIOD, y = rnd() * PERIOD, depth = Math.min(0.9, pop.depth * (0.6 + 0.8 * rnd()));
      const rr = Math.min(pop.maxRadius, Math.max(0.05, -Math.log(1 - rnd()) * pop.radius));
      for (let pit = 1 + Math.floor(rnd() * 3); pit > 0; pit--) {
        const a = rnd() * 2 * Math.PI, off = rr * 0.7 * rnd(), pr = rr * (0.5 + 0.5 * rnd());
        const px = wrap(x + off * Math.cos(a), PERIOD), py = wrap(y + off * Math.sin(a), PERIOD);
        maxR = Math.max(maxR, pr);
        pores[Math.floor(py) * cells + Math.floor(px)].push({ x: px, y: py, r: pr, depth });
      }
    }
  }
  const reach = Math.ceil(maxR);
  return {
    period: PERIOD,
    at(x, y) {
      let k = 1 + sandAmp * sand(x, y);
      for (const o of octaves) k += o.amp * o.f(x, y);
      const cx = Math.floor(x), cy = Math.floor(y);
      for (let dy = -reach; dy <= reach; dy++) for (let dx = -reach; dx <= reach; dx++) {
        const i = cx + dx, j = cy + dy, list = pores[wrap(j, cells) * cells + wrap(i, cells)];
        if (!list.length) continue;
        // The pores' own positions, moved by the wrap to lie by (x, y).
        const ox = i - wrap(i, cells), oy = j - wrap(j, cells);
        for (const p of list) {
          const d = Math.hypot(x - p.x - ox, y - p.y - oy);
          if (d < p.r) k *= 1 - p.depth * smooth(1 - d / p.r);
        }
      }
      return [base[0] * k, base[1] * k, base[2] * k];
    },
  };
}

/** Where a bead's dome is lit from, in the texture's plane. */
const LIGHT = [-0.6, -0.8] as const;

/** A smooth minimum of `a` and `b`, rounding the corner where they meet over `k`. */
function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - (h * h * k) / 4;
}

/** EPS: beads on a jittered grid, each the points nearest its seed (Voronoi)
 *  within its own disk, its corners rounded; lighter to its middle and a shade
 *  of its own. Between them the blue of the gaps, darker the deeper the gap — the
 *  voids where three beads meet. */
function beadsField(r: BeadsRecipe): SurfaceField {
  const rnd = randoms(r.seed);
  const n = Math.max(1, Math.round(PERIOD / r.bead)), s = PERIOD / n, m = n * n;
  const sx = new Float64Array(m), sy = new Float64Array(m), rad = new Float64Array(m), shade = new Float64Array(m), tint = new Float64Array(m);
  for (let i = 0; i < m; i++) {
    sx[i] = ((i % n) + 0.5 + (rnd() - 0.5) * 2 * r.jitter) * s;
    sy[i] = (Math.floor(i / n) + 0.5 + (rnd() - 0.5) * 2 * r.jitter) * s;
    rad[i] = r.fill * s * (0.85 + 0.3 * rnd());
    shade[i] = 1 + (rnd() - 0.5) * 2 * r.spread;
    tint[i] = rnd();
  }
  const centre = rgbOf(r.centre), rim = rgbOf(r.rim), gap = rgbOf(r.gap), hollow = rgbOf(r.void);
  const grain = gradientNoise(0.5, rnd), w = r.gapWidth / 2, round = r.round * s;
  return {
    period: PERIOD,
    at(x, y) {
      const ci = Math.floor(x / s), cj = Math.floor(y / s);
      // The three nearest seeds, each where the wrap puts it by (x, y).
      let d1 = Infinity, d2 = Infinity, d3 = Infinity, a = 0, ax = 0, ay = 0, bx = 0, by = 0, cx = 0, cy = 0;
      for (let dj = -2; dj <= 2; dj++) for (let di = -2; di <= 2; di++) {
        const i = ci + di, j = cj + dj, wi = wrap(i, n), wj = wrap(j, n), k = wj * n + wi;
        const px = sx[k] + (i - wi) * s, py = sy[k] + (j - wj) * s, d = (x - px) * (x - px) + (y - py) * (y - py);
        if (d < d1) { d3 = d2; cx = bx; cy = by; d2 = d1; bx = ax; by = ay; d1 = d; a = k; ax = px; ay = py; }
        else if (d < d2) { d3 = d2; cx = bx; cy = by; d2 = d; bx = px; by = py; }
        else if (d < d3) { d3 = d; cx = px; cy = py; }
      }
      // How far inside its bead (x, y) is: from the borders it shares with the
      // next two, rounded where they meet, and from the bead's own disk.
      const eb = (d2 - d1) / (2 * Math.hypot(bx - ax, by - ay)), ec = (d3 - d1) / (2 * Math.hypot(cx - ax, cy - ay));
      const e = smin(smin(eb, ec, round), rad[a] - Math.sqrt(d1), round);
      const g = 1 + 0.03 * grain(x, y);
      let col: RGB;
      if (e > w) {
        // A dome: lighter to its middle, lit on the side towards the light.
        const up = smooth((e - w) / (0.2 * s)), lit = mix(rim, centre, up);
        const dl = Math.sqrt(d1) || 1, side = ((x - ax) * LIGHT[0] + (y - ay) * LIGHT[1]) / dl;
        const t = 0.2 * tint[a], sh = shade[a] * g * (1 + 0.09 * side * (1 - up));
        col = [(lit[0] + (gap[0] - lit[0]) * t) * sh, (lit[1] + (gap[1] - lit[1]) * t) * sh, (lit[2] + (gap[2] - lit[2]) * t) * sh];
      } else {
        const deep = mix(gap, hollow, smooth((w - e) / (0.15 * s)));
        col = [deep[0] * g, deep[1] * g, deep[2] * g];
      }
      // Antialias the bead's edge over a tenth of a mm.
      if (Math.abs(e - w) < 0.05) {
        const other = e > w ? mix(gap, hollow, 0) : mix(rim, centre, 0).map((v) => v * shade[a]) as RGB;
        col = mix(other, col, 0.5 + Math.abs(e - w) * 10);
      }
      return col;
    },
  };
}

const fields = new Map<SurfaceRecipe, SurfaceField>();

/** A recipe's surface, built once. */
export function surfaceField(r: SurfaceRecipe): SurfaceField {
  let f = fields.get(r);
  if (!f) fields.set(r, f = r.kind === 'cement' ? cementField(r) : beadsField(r));
  return f;
}

/** `w × h` pixels of `colour(x, y)` at pixel centres, `kx` and `ky` to the mm. */
function paint(w: number, h: number, kx: number, ky: number, colour: (x: number, y: number) => RGB): Raster {
  const data = new Uint8ClampedArray(w * h * 4);
  for (let py = 0; py < h; py++) for (let px = 0; px < w; px++) {
    const c = colour((px + 0.5) / kx, (py + 0.5) / ky), o = 4 * (py * w + px);
    data[o] = c[0]; data[o + 1] = c[1]; data[o + 2] = c[2]; data[o + 3] = 255;
  }
  return { width: w, height: h, data };
}

/** One period of a surface, about `pxPerMm` to the mm (exactly a period across
 *  its whole pixels): it tiles. */
export function rasterize(f: SurfaceField, pxPerMm: number): Raster {
  const n = Math.round(f.period * pxPerMm), k = n / f.period;
  return paint(n, n, k, k, (x, y) => f.at(x, y));
}

/** A panel `thickness` mm thick cut across: across it (x, from its outside face)
 *  its `core` between skins of `skin` `skinMm` thick; down it one period of both,
 *  so it tiles that way. */
export function cutRaster(core: SurfaceField, skin: SurfaceField, skinMm: number, thickness: number, pxPerMm: number): Raster {
  const period = Math.max(core.period, skin.period);
  const w = Math.max(2, Math.round(thickness * pxPerMm)), h = Math.round(period * pxPerMm);
  return paint(w, h, w / thickness, h / period, (x, y) => (x < skinMm || x > thickness - skinMm ? skin : core).at(x, y));
}

/** A raster's average colour. */
export function meanColour(r: Raster): RGB {
  const s: RGB = [0, 0, 0], n = r.width * r.height;
  for (let i = 0; i < n; i++) for (let c = 0; c < 3; c++) s[c] += r.data[4 * i + c] / n;
  return s;
}
